"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { cn, btn3d } from "@/lib/utils"
import { History, Check, X, Clock } from "lucide-react"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { practiceService, QuestionAttempt } from "@/services/practice"
import { formatDistanceToNow } from "date-fns"

interface QuestionHistoryButtonProps {
    questionId: number
    minimal?: boolean
}

export function QuestionHistoryButton({ questionId, minimal = false }: QuestionHistoryButtonProps) {
    const [attempts, setAttempts] = useState<QuestionAttempt[]>([])
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (open && questionId) {
            fetchAttempts()
        }
    }, [open, questionId])

    const fetchAttempts = async () => {
        setLoading(true)
        try {
            const data = await practiceService.getQuestionAttempts(questionId)
            setAttempts(data)
        } catch (error) {
            console.error("Failed to fetch attempts:", error)
        } finally {
            setLoading(false)
        }
    }

    const formatTime = (seconds: number | null) => {
        if (!seconds) return "N/A"
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
    }

    const getOrdinal = (index: number) => {
        const ordinals = ["1st", "2nd", "3rd"]
        return ordinals[index] || `${index + 1}th`
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={minimal
                        ? cn("h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-2 rounded-md bg-card border-border text-foreground hover:bg-muted relative font-medium text-sm", btn3d)
                        : cn("fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-card border-2", btn3d)}
                    aria-label="View attempt history"
                >
                    <History className="h-4 w-4 md:h-5 md:w-5" />
                    {minimal && <span className="hidden md:inline font-medium text-sm">History</span>}
                    {attempts.length > 0 && (
                        <span className={minimal
                            ? "absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold"
                            : "absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-semibold"}>
                            {attempts.length}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end" side="top">
                <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                        Recent Attempts
                    </h3>

                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            Loading...
                        </div>
                    ) : attempts.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            No previous attempts for this question
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {attempts.map((attempt, index) => (
                                <div
                                    key={attempt.id}
                                    className={`p-3 rounded-lg border-2 ${attempt.isCorrect
                                        ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
                                        : "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                                        }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold text-muted-foreground">
                                                {getOrdinal(index)} Attempt
                                            </span>
                                            {attempt.isCorrect ? (
                                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            ) : (
                                                <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            {formatDistanceToNow(new Date(attempt.attemptedAt), { addSuffix: true })}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Answer: </span>
                                            <span className="font-semibold">{attempt.selectedAnswer}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <Clock className="h-3 w-3" />
                                            <span className="text-xs">{formatTime(attempt.timeTakenSeconds)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}
